import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { ActivityIcon, MessagesSquareIcon, XIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSpecs } from "../../api/registry-queries";
import { useRuns } from "../../api/run-queries";
import { RouteLink } from "../../app/route-link";
import { Button } from "../../ui/button";
import { EmptyState } from "../../ui/empty-state";
import { ErrorState } from "../../ui/error-state";
import { NativeSelect } from "../../ui/native-select";
import { PageHeader } from "../../ui/page-header";
import { Skeleton } from "../../ui/skeleton";
import { type RunsSearch, routes, useEnvironment } from "../context";
import { RunsTable } from "./runs-table";

/** Runs of a project (useRuns polls while one is queued or running). */
export function RunsList({
  workspace,
  project,
  filters,
  empty,
}: {
  workspace: string;
  project: string;
  filters: RunsSearch;
  empty?: React.ReactNode;
}) {
  const { t } = useTranslation();
  const { environments } = useEnvironment(workspace);
  const runs = useRuns(workspace, project, filters);
  if (runs.error) return <ErrorState error={runs.error} />;
  if (!runs.data) return <Skeleton className="h-40" aria-label={t("runs.loading")} />;
  if (runs.data.length === 0) {
    return (
      empty ?? (
        <EmptyState icon={ActivityIcon} title={t("runs.empty.title")} text={t("runs.empty.text")} />
      )
    );
  }
  return (
    <RunsTable
      workspace={workspace}
      project={project}
      runs={runs.data}
      environments={environments}
    />
  );
}

/** /w/$workspace/p/$project/runs: the project's runs, filtered by agent and conversation in the URL. */
export function RunsPage() {
  const { t } = useTranslation();
  const { workspace, project } = useParams({ strict: false }) as {
    workspace: string;
    project: string;
  };
  const search = useSearch({ strict: false }) as RunsSearch;
  const navigate = useNavigate();
  const agents = useSpecs(workspace, { kind: "agent", project });
  const set = (change: { agent?: string | undefined; conversation?: string | undefined }) =>
    void navigate({
      to: ".",
      search: (prev: RunsSearch) => clean({ ...prev, ...change }),
      replace: true,
    });
  return (
    <>
      <PageHeader title={t("runs.title")} description={t("runs.description", { project })} />
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <label htmlFor="runs-agent" className="flex items-center gap-2 text-sm text-ink-2">
          {t("runs.filters.agent")}
          <NativeSelect
            id="runs-agent"
            value={search.agent ?? ""}
            onChange={(event) => set({ agent: event.target.value || undefined })}
          >
            <option value="">{t("runs.filters.allAgents")}</option>
            {(agents.data ?? []).map((agent) => (
              <option key={agent.name} value={agent.name}>
                {agent.name}
              </option>
            ))}
          </NativeSelect>
        </label>
        {search.conversation && (
          <Button size="sm" variant="outline" onClick={() => set({ conversation: undefined })}>
            {t("runs.filters.conversation", { id: search.conversation.slice(0, 8) })}
            <XIcon aria-label={t("runs.filters.clear")} />
          </Button>
        )}
      </div>
      <RunsList
        workspace={workspace}
        project={project}
        filters={search}
        empty={
          <EmptyState
            icon={ActivityIcon}
            title={t("runs.empty.title")}
            text={t("runs.empty.text")}
            action={
              <Button size="sm" variant="outline" asChild>
                <RouteLink
                  to={routes.chat(workspace, project, search.agent ? { agent: search.agent } : {})}
                >
                  <MessagesSquareIcon aria-hidden />
                  {t("runs.openChat")}
                </RouteLink>
              </Button>
            }
          />
        }
      />
    </>
  );
}

const clean = (search: Record<string, string | undefined>): RunsSearch =>
  Object.fromEntries(
    Object.entries(search).filter(([, value]) => value !== undefined),
  ) as RunsSearch;
